import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useDeepLinkHighlight from '../hooks/useDeepLinkHighlight';
import {
  Inbox, Package, CheckCircle2, AlertTriangle, RefreshCw,
  ArrowRight, Loader2, Truck, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return ''; }
}

const TABS = [
  { key: 'pending', label: 'To Release', icon: Package, color: 'amber' },
  { key: 'released', label: 'Awaiting Pickup', icon: Truck, color: 'blue' },
  { key: 'feedback', label: 'Awaiting Feedback', icon: AlertTriangle, color: 'orange' },
  { key: 'return', label: 'To Confirm Return', icon: RefreshCw, color: 'purple' },
];

const COLOR_MAP = {
  amber: { bg: 'bg-amber-500', bgLight: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', pill: 'bg-amber-100 text-amber-800' },
  blue: { bg: 'bg-blue-500', bgLight: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', pill: 'bg-blue-100 text-blue-800' },
  orange: { bg: 'bg-orange-500', bgLight: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300', pill: 'bg-orange-100 text-orange-800' },
  purple: { bg: 'bg-purple-500', bgLight: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300', pill: 'bg-purple-100 text-purple-800' },
};

function categorize(tickets) {
  const pending = [];
  const released = [];
  const feedback = [];
  const ret = [];
  for (const t of tickets) {
    if (t.status === 'pending') pending.push(t);
    else if (t.status === 'released') released.push(t);
    else if (t.status === 'received' && !t.usage_status) feedback.push(t);
    else if (t.usage_status === 'reported' && (t.qty_to_return || 0) > 0) ret.push(t);
  }
  return { pending, released, feedback, return: ret };
}

export default function StoresQueuePage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [releaseDialog, setReleaseDialog] = useState(null);
  const [releaseQty, setReleaseQty] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useDeepLinkHighlight('ticket', 'stores-ticket-', [loading, tickets.length, activeTab]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/parts-release`, { headers: { Authorization: `Bearer ${token}` } });
      setTickets(res.data || []);
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // When linked in via ?ticket=<id>, auto-switch to the tab that contains it
  useEffect(() => {
    const target = searchParams.get('ticket');
    if (!target || tickets.length === 0) return;
    const t = tickets.find(x => x.id === target);
    if (!t) return;
    if (t.status === 'pending') setActiveTab('pending');
    else if (t.status === 'released') setActiveTab('released');
    else if (t.status === 'received' && !t.usage_status) setActiveTab('feedback');
    else if (t.usage_status === 'reported' && (t.qty_to_return || 0) > 0) setActiveTab('return');
  }, [searchParams, tickets]);

  const grouped = useMemo(() => categorize(tickets), [tickets]);
  const counts = {
    pending: grouped.pending.length,
    released: grouped.released.length,
    feedback: grouped.feedback.length,
    return: grouped.return.length,
  };

  const visible = useMemo(() => {
    const list = grouped[activeTab] || [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(t =>
      (t.ticket_number || '').toLowerCase().includes(q) ||
      (t.part_name || '').toLowerCase().includes(q) ||
      (t.door_no || '').toLowerCase().includes(q) ||
      (t.part_number || '').toLowerCase().includes(q)
    );
  }, [grouped, activeTab, search]);

  const totalPending = counts.pending + counts.feedback + counts.return;

  const openReleaseDialog = (ticket) => {
    setReleaseDialog(ticket);
    setReleaseQty(String(ticket.quantity_requested || 1));
  };

  const handleRelease = async () => {
    if (!releaseDialog) return;
    const qty = parseInt(releaseQty, 10);
    if (!qty || qty <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/parts-release/${releaseDialog.id}/release?quantity_released=${qty}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Released ${qty} ${releaseDialog.part_name}`);
      setReleaseDialog(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Release failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (ticket) => {
    if (!window.confirm(`Reject request for ${ticket.part_name}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/parts-release/${ticket.id}/reject?reason=Rejected%20via%20stores%20queue`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reject failed');
    }
  };

  const handleConfirmReturn = async (ticket) => {
    if (!window.confirm(`Confirm physical return of ${ticket.qty_to_return} × ${ticket.part_name}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/parts-release/${ticket.id}/confirm-return`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Return confirmed, stock updated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Confirm failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="stores-queue-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-purple-500/15 border border-purple-400/40 flex items-center justify-center">
            <Inbox className="h-5 w-5 text-purple-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Stores Queue</h1>
            <p className="text-sm text-slate-600">
              {totalPending === 0 ? 'All caught up!' : `${totalPending} action${totalPending === 1 ? '' : 's'} need attention`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search ticket, part, door no…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-48 sm:w-64"
            data-testid="stores-queue-search"
          />
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="stores-queue-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {TABS.map(tab => {
          const c = COLOR_MAP[tab.color];
          const active = activeTab === tab.key;
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`stores-queue-tab-${tab.key}`}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 transition-all ${
                active
                  ? `${c.bgLight} ${c.border} ${c.text} shadow-sm`
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="font-semibold text-sm">{tab.label}</span>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${active ? c.pill : 'bg-slate-100 text-slate-600'}`}>
                {counts[tab.key]}
              </span>
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin inline-block mr-2" /> Loading tickets…
          </div>
        ) : visible.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold">Nothing here</p>
            <p className="text-sm text-slate-500 mt-1">
              {search ? 'Try a different search term' : 'No tickets in this queue right now'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {visible.map(t => (
              <TicketRow
                key={t.id}
                ticket={t}
                activeTab={activeTab}
                onRelease={() => openReleaseDialog(t)}
                onReject={() => handleReject(t)}
                onConfirmReturn={() => handleConfirmReturn(t)}
                onOpen={() => navigate(`/inventory/parts-release/pending?ticket=${t.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Release dialog */}
      <Dialog open={!!releaseDialog} onOpenChange={(open) => !open && setReleaseDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="stores-queue-release-dialog">
          <DialogHeader>
            <DialogTitle>Release Parts</DialogTitle>
          </DialogHeader>
          {releaseDialog && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-slate-500">Ticket:</span> <span className="font-semibold">{releaseDialog.ticket_number}</span></p>
                <p><span className="text-slate-500">Part:</span> <span className="font-semibold">{releaseDialog.part_name}</span></p>
                <p><span className="text-slate-500">For:</span> <span className="font-semibold">{releaseDialog.door_no}</span></p>
                <p><span className="text-slate-500">Requested:</span> <span className="font-semibold">{releaseDialog.quantity_requested}</span></p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">Quantity to release</label>
                <Input
                  type="number"
                  min="1"
                  value={releaseQty}
                  onChange={e => setReleaseQty(e.target.value)}
                  data-testid="stores-queue-release-qty"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseDialog(null)}>Cancel</Button>
            <Button
              onClick={handleRelease}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="stores-queue-confirm-release"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Confirm Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TicketRow({ ticket, activeTab, onRelease, onReject, onConfirmReturn, onOpen }) {
  const renderActions = () => {
    if (activeTab === 'pending') {
      return (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onReject} data-testid={`stores-reject-${ticket.id}`}>Reject</Button>
          <Button size="sm" onClick={onRelease} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`stores-release-${ticket.id}`}>
            Release
          </Button>
        </div>
      );
    }
    if (activeTab === 'return') {
      return (
        <Button size="sm" onClick={onConfirmReturn} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`stores-confirm-return-${ticket.id}`}>
          <CheckCircle2 className="h-4 w-4 mr-1" />
          Confirm Return
        </Button>
      );
    }
    return (
      <Button size="sm" variant="ghost" onClick={onOpen} data-testid={`stores-view-${ticket.id}`}>
        View <ChevronRight className="h-4 w-4 ml-1" />
      </Button>
    );
  };

  return (
    <div className="p-4 hover:bg-slate-50 transition-colors" data-testid={`stores-ticket-${ticket.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-slate-500">{ticket.ticket_number}</span>
            <span className="text-xs text-slate-300">•</span>
            <span className="text-xs text-slate-500">{timeAgo(ticket.created_at)}</span>
            {ticket.priority && ticket.priority !== 'normal' && (
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                ticket.priority === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {ticket.priority.toUpperCase()}
              </span>
            )}
          </div>
          <p className="font-semibold text-slate-900 truncate mt-0.5">{ticket.part_name}</p>
          <div className="flex items-center gap-3 text-sm text-slate-600 mt-0.5 flex-wrap">
            <span className="flex items-center gap-1">
              <Truck className="h-3.5 w-3.5" />
              {ticket.door_no}
            </span>
            <span>
              Qty: <span className="font-semibold text-slate-900">
                {activeTab === 'return' ? `${ticket.qty_to_return} to return` :
                 activeTab === 'feedback' ? `${ticket.quantity_released || ticket.quantity_requested} released` :
                 ticket.quantity_requested}
              </span>
            </span>
            {ticket.part_number && <span className="text-xs text-slate-400">{ticket.part_number}</span>}
          </div>
          {activeTab === 'feedback' && (
            <p className="text-xs text-orange-700 mt-1 italic">Mechanic needs to confirm usage / return</p>
          )}
        </div>
        <div className="flex-shrink-0">{renderActions()}</div>
      </div>
    </div>
  );
}
